import type { ApiPermissionGrantV3 } from '@/types/permissionGrants.v3';
import { PERMISSION_GRANT_KEYS, type GrantKey, type PermissionDraftMap, type PermissionScope } from '@/types/permissionModules';
import { createEmptyPermissionsGrants, type PermissionsGrants } from '@/types/permissionsGrants';
import type { ApiMeV3 } from '@/types/users.v3';
import { grantRows, pairedViewKey } from '@/utils/UserPermissions';

const KNOWN_GRANT_KEYS: ReadonlySet<string> = new Set(PERMISSION_GRANT_KEYS);

const SCOPE_BY_KEY = new Map<GrantKey, PermissionScope>(
  grantRows().map((row) => [row.key, row.scope]),
);

interface NormalizedGrant {
  permission: GrantKey;
  teamTwrn: string | null;
}

export interface MappedMeV3 {
  grants: PermissionsGrants;
  unknown: string[];
}

function normalize(grants: ApiPermissionGrantV3[]): {
  normalized: NormalizedGrant[];
  unknown: string[];
} {
  const unknown = new Set<string>();
  const normalized: NormalizedGrant[] = [];

  for (const grant of grants) {
    // Roster VIEW is derived. A row for it must not become a base grant.
    if (grant.permission === 'viewRoster') continue;

    if (!KNOWN_GRANT_KEYS.has(grant.permission)) {
      unknown.add(grant.permission);
      continue;
    }

    const permission = grant.permission as GrantKey;
    const teamTwrn = SCOPE_BY_KEY.get(permission) === 'org' ? null : grant.team_twrn;
    normalized.push({
      permission,
      teamTwrn,
    });
  }

  const implied: NormalizedGrant[] = [];
  for (const grant of normalized) {
    const viewKey = pairedViewKey(grant.permission);
    if (!viewKey) continue;

    const alreadyHeld = [...normalized, ...implied].some((existing) => (
      existing.permission === viewKey && existing.teamTwrn === grant.teamTwrn
    ));
    if (alreadyHeld) continue;

    implied.push({
      permission: viewKey,
      teamTwrn: grant.teamTwrn,
    });
  }

  return {
    normalized: [...normalized, ...implied],
    unknown: [...unknown],
  };
}

function holdsGrant(
  grants: NormalizedGrant[],
  key: GrantKey,
  activeTeamTwrn: string | null,
  isMemberOfActiveTeam: boolean,
): boolean {
  if (SCOPE_BY_KEY.get(key) === 'org') {
    return grants.some((grant) => grant.permission === key);
  }

  if (!isMemberOfActiveTeam || !activeTeamTwrn) return false;

  return grants.some((grant) => (
    grant.permission === key
    && (grant.teamTwrn == null || grant.teamTwrn === activeTeamTwrn)
  ));
}

function applyDerivedPermissions(
  grants: PermissionsGrants,
  holdsAnyOtherGrant: boolean,
  isMemberOfActiveTeam: boolean,
) {
  // All three budget views. Partial access hides the total.
  grants.canViewTotalEarnings = grants.viewInstitutionalNil
    && grants.viewThirdPartyNil
    && grants.viewScholarships;
  // Any other grant, plus membership on the team being checked.
  grants.viewRoster = holdsAnyOtherGrant && isMemberOfActiveTeam;
  grants.viewSettingsScholarships = grants.editScholarships;
  // Either agreements EDIT grant opens contract templates.
  grants.viewContractTemplates = grants.editRevShareAgreements || grants.editScholarshipAgreements;
  grants.viewSettingsNotifications = grants.editPayments;
  grants.viewUserPermissions = grants.isSuperAdmin;
}

export interface GrantsFromDraftContext {
  isSuperAdmin: boolean;
  hasAppAccess: boolean;
  isMemberOfActiveTeam: boolean;
}

/**
 * Builds the grant object the dev mock writes over the mapper.
 * Identity flags stay on the real `/me` values. Derived checks follow the draft.
 * The super-admin short-circuit does not run, so a super admin can simulate a narrower set.
 */
export function grantsFromDraft(
  draft: PermissionDraftMap,
  context: GrantsFromDraftContext,
): PermissionsGrants {
  const grants = createEmptyPermissionsGrants();
  grants.isSuperAdmin = context.isSuperAdmin;
  grants.hasAppAccess = context.hasAppAccess;

  let holdsAnyOtherGrant = false;
  for (const key of PERMISSION_GRANT_KEYS) {
    if (key === 'viewRoster') continue;

    const held = draft[key] === true;
    grants[key] = held;
    if (held) holdsAnyOtherGrant = true;
  }

  applyDerivedPermissions(grants, holdsAnyOtherGrant, context.isMemberOfActiveTeam);
  return grants;
}

/**
 * A Super Admin has every check, including hasAppAccess with an empty team list
 * and an empty grant list. Stays on the v3 path only. Legacy Super Admin access
 * is role-based.
 */
function withSuperAdminShortCircuit(grants: PermissionsGrants): PermissionsGrants {
  if (!grants.isSuperAdmin) return grants;

  const open = createEmptyPermissionsGrants();
  for (const key of Object.keys(open) as (keyof PermissionsGrants)[]) {
    open[key] = true;
  }
  return open;
}

/**
 * Maps a v3 `/me` envelope into `PermissionsGrants` for one team.
 * Org-scoped grants keep no team dimension. EDIT implies VIEW inside the same
 * scope bucket. Derived checks run after the base grants, and the Super Admin
 * short-circuit runs last.
 */
export function mapMeV3Grants(
  me: ApiMeV3 | null | undefined,
  activeTeamTwrn: string | null,
): MappedMeV3 {
  if (!me) {
    return {
      grants: createEmptyPermissionsGrants(),
      unknown: [],
    };
  }

  const { normalized, unknown } = normalize(me.permission_grants ?? []);
  const isMemberOfActiveTeam = activeTeamTwrn != null
    && me.teams.some((team) => team.twrn === activeTeamTwrn);

  const grants = createEmptyPermissionsGrants();
  grants.isSuperAdmin = !!me.user?.is_super_admin;
  grants.hasAppAccess = me.teams.length > 0;

  for (const key of PERMISSION_GRANT_KEYS) {
    if (key === 'viewRoster') continue;
    grants[key] = holdsGrant(normalized, key, activeTeamTwrn, isMemberOfActiveTeam);
  }

  applyDerivedPermissions(grants, normalized.length > 0, isMemberOfActiveTeam);

  return {
    grants: withSuperAdminShortCircuit(grants),
    unknown,
  };
}
